<template>
    <div class="modal" :class="{ 'is-open': open }">
        <div class="modal-box modal-box--pallet-details">
            <div class="modal-box-header" style="padding-bottom: 0;">
                <button type="button" @click="$emit('close')" class="btn btn-plain btn-plain-wicon">
                    <md-icon>keyboard_arrow_left</md-icon>
                </button>
                <div class="modal-title-stack">
                    <span class="title">Raklap részletek</span>
                    <button type="button" class="pallet-help-link" @click.prevent="calculationHelpOpen = true">
                        Miért, mikor és hogyan számol a rendszer?
                    </button>
                </div>
            </div>

            <div class="modal-box-body">

                <!-- Section A: Per-product breakdown -->
                <div class="pallet-section" v-if="palletProducts.length > 0">
                    <h4 class="pallet-section-title">Termékek raklap hozzájárulása</h4>
                    <table class="pallet-table">
                        <thead>
                            <tr>
                                <th>Termék</th>
                                <th>Raklap típus</th>
                                <th>Szabály</th>
                                <th class="text-right">Mennyiség</th>
                                <th class="text-right th-tooltip" title="A megadott mennyiség szorozva az egységváltó szorzóval (pl. m² → db)">Norm. menny.</th>
                                <th class="text-right th-tooltip" title="Ekkora normalizált mennyiségtől kezdve számít bele a raklap kalkulációba">Min. küszöb</th>
                                <th class="text-right th-tooltip" title="Ennyi normalizált egységre jut egy raklap">Osztó</th>                                <th class="text-right th-tooltip" title="A termék 1 normalizált egységének súlya kilogrammban">Egységsúly (kg)</th>                                <th class="text-right th-tooltip" title="Norm. mennyiség / Osztó — törtszám, felfelé kerekítés előtt">Tört raklap</th>
                                <th class="text-right th-tooltip" title="Végleges hozzájárulás ennél a terméknél (kerekítés után, ha szükséges)">Hozzájárulás</th>
                                <th class="text-right th-tooltip" title="A töredék raklap hány kg-ját teszi ki ez a termék — (Norm. menny. mod Osztó) × Egységsúly. Megmutatja, mennyi súly kerül az extra (töredék) raklapra.">Töredék súly (kg)</th>
                                <th class="text-right th-tooltip" title="Norm. mennyiség × egységsúly (kg). Üres, ha a terméknek nincs súlya.">Összsúly (kg)</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="row in palletProducts" :key="row.id">
                                <td>{{ row.name }}</td>
                                <td :title="row.palletDescription || row.palletName">{{ row.palletDescription }}</td>
                                <td>
                                    <span class="rule-badge" :class="'rule-badge--' + row.ruleCode" :title="row.ruleReason">
                                        {{ row.ruleLabel }}
                                    </span>
                                </td>
                                <td class="text-right">{{ row.amount }} {{ row.unitName }}</td>
                                <td class="text-right">{{ row.normalizedAmount }} {{ row.normalizedUnitName }}</td>
                                <td class="text-right">
                                    <span v-if="row.pallet.calculateAbove !== null">{{ row.pallet.calculateAbove }} {{ row.normalizedUnitName }}</span>
                                    <span v-else class="text-muted">–</span>
                                </td>
                                <td class="text-right">{{ row.pallet.divider }}</td>
                                <td class="text-right">{{ row.weight !== null ? row.weight : '–' }}</td>
                                <td class="text-right">
                                    <span v-if="row.belowThreshold" class="text-muted">– (küszöb alatt)</span>
                                    <span v-else>{{ row.fraction }}</span>
                                </td>
                                <td class="text-right">
                                    <span v-if="row.belowThreshold" class="text-muted">0</span>
                                    <strong v-else>{{ row.contribution }}</strong>
                                </td>
                                <td class="text-right">
                                    <span v-if="row.extraWeight !== null">{{ row.extraWeight.toFixed(2) }}</span>
                                    <span v-else class="text-muted">–</span>
                                    <span v-if="row.totalFractionalWeight !== null" class="text-muted">
                                        / össz.: {{ row.totalFractionalWeight.toFixed(2) }}
                                    </span>
                                </td>
                                <td class="text-right">{{ row.totalWeight !== null ? row.totalWeight.toFixed(2) : '–' }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div class="pallet-section pallet-empty" v-else>
                    <p>Nincs raklap-köteles termék hozzáadva.</p>
                </div>

                <!-- Section B: Weight-based pallet rule -->
                <div class="pallet-section" v-if="weightRuleProducts.length > 0">
                    <h4 class="pallet-section-title">Súly alapú raklap szabály</h4>
                    <table class="pallet-table">
                        <thead>
                            <tr>
                                <th>Termék</th>
                                <th>Szabály</th>
                                <th class="text-right">Mennyiség</th>
                                <th class="text-right th-tooltip" title="A megadott mennyiség szorozva az egységváltó szorzóval (pl. m² → db)">Norm. menny.</th>
                                <th class="text-right th-tooltip" title="Az adott termék 1 normalizált egységének súlya kilogrammban">Egységsúly (kg)</th>
                                <th class="text-right th-tooltip" title="Norm. mennyiség × egységsúly — ez az érték adódik össze a küszöbhöz való összehasonlításhoz">Összsúly (kg)</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="row in weightRuleProducts" :key="row.id">
                                <td>{{ row.name }}</td>
                                <td>
                                    <span class="rule-badge" :class="'rule-badge--' + row.ruleCode" :title="row.ruleReason">
                                        {{ row.ruleLabel }}
                                    </span>
                                </td>
                                <td class="text-right">{{ row.amount }} {{ row.unitName }}</td>
                                <td class="text-right">{{ row.normalizedAmount }} {{ row.normalizedUnitName }}</td>
                                <td class="text-right">{{ row.weight }}</td>
                                <td class="text-right">{{ row.totalWeight.toFixed(2) }}</td>
                            </tr>
                        </tbody>
                    </table>
                    <div class="weight-rule-summary">
                        <span>Összsúly: <strong>{{ weightRuleTotalWeight.toFixed(2) }} kg</strong></span>
                        <span>Küszöb: <strong>{{ weightFallbackRule.minWeight }} kg</strong></span>
                        <span v-if="weightRuleActive" class="weight-rule-active">
                            ✓ Küszöb elérve → +{{ weightFallbackRule.maxPallets }} raklap hozzáadva
                        </span>
                        <span v-else class="weight-rule-inactive">
                            Nem érte el a küszöböt (hiány: {{ (weightFallbackRule.minWeight - weightRuleTotalWeight).toFixed(2) }} kg)
                        </span>
                    </div>
                </div>

                <!-- Section C: Summary per pallet type -->
                <div class="pallet-section" v-if="pallets.length > 0">
                    <h4 class="pallet-section-title">Összesítés raklap típusonként</h4>
                    <table class="pallet-table">
                        <thead>
                            <tr>
                                <th>Raklap típus</th>
                                <th class="text-right">Nyers összeg</th>
                                <th class="text-right">Kerekítve (felfelé)</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="pallet in pallets" :key="pallet.id">
                                <td :title="pallet.label || pallet.name">{{ pallet.label }}</td>
                                <td class="text-right">{{ pallet.amountNoRounded !== undefined ? Number(pallet.amountNoRounded).toFixed(4) : '–' }}</td>
                                <td class="text-right"><strong>{{ pallet.amount }}</strong></td>
                            </tr>
                        </tbody>
                    </table>
                </div>

            </div>
        </div>
        <div class="pallet-help-overlay" v-if="calculationHelpOpen">
            <div class="pallet-help-box">
                <div class="pallet-help-header">
                    <div>
                        <h3>Raklap számítás magyarázat</h3>
                        <p>A táblázatok azt mutatják, hogy egy termék melyik szabály miatt ad vagy nem ad raklapot.</p>
                    </div>
                    <button type="button" class="btn btn-plain" @click="calculationHelpOpen = false">
                        <md-icon>close</md-icon>
                    </button>
                </div>

                <div class="pallet-help-content">
                    <section>
                        <h4>Miért van több szabály?</h4>
                        <p>
                            Nem minden termékből lesz ugyanúgy raklap. Van termék, ahol pontosan tudjuk, mennyi fér egy raklapra.
                            Van termék, ahol csak a súly számít. Van olyan is, ahol kis mennyiség még nem indít külön raklapot.
                        </p>
                    </section>

                    <section>
                        <h4>Mikor melyik szabály látszik?</h4>
                        <ul>
                            <li>
                                <span class="rule-badge rule-badge--normal">Normál</span>
                                A terméknek van raklap osztója, elérte a minimumot, ezért a mennyiség osztóval számolódik.
                            </li>
                            <li>
                                <span class="rule-badge rule-badge--below_threshold">Küszöb alatt</span>
                                A terméknek van raklap adata, de a mennyisége nem érte el a raklap számítás minimumát, ezért a felső táblázatban 0.
                            </li>
                            <li>
                                <span class="rule-badge rule-badge--fraction_remainder">Tört &lt; 25 kg</span>
                                A termék tört raklap maradéka a többi hasonló maradékkal együtt 25 kg alatt marad, ezért meglévő raklapra kerül.
                            </li>
                            <li>
                                <span class="rule-badge rule-badge--threshold_weight_fallback">Küszöb alatt -&gt; súly</span>
                                Ugyanaz a küszöb alatti termék az alsó táblázatban is megjelenik, mert a teljes súlya beleszámít a súly alapú szabályba.
                            </li>
                            <li>
                                <span class="rule-badge rule-badge--weight_fallback">Súly</span>
                                A terméknek nincs normál raklap számítása, ezért az alsó táblázatban a teljes súlya számít.
                            </li>
                        </ul>
                    </section>

                    <section>
                        <h4>Mit jelent a töredék súlynál az össz. érték?</h4>
                        <p>
                            A <strong>/ össz.</strong> érték csak a tört raklap szabálynál jelenik meg. Ez nem az adott termék saját súlya,
                            hanem az összes olyan termék tört raklap maradékának közös súlya, amely ugyanebbe a szabályba került.
                        </p>
                        <p>
                            Példa: ha az egyik terméknél 3 kg, a másiknál 8 kg tört maradék van, akkor mindkét sorban
                            <strong>/ össz.: 11.00</strong> látszik. A rendszer ezt a közös értéket hasonlítja a 25 kg-os határhoz.
                        </p>
                    </section>

                    <section v-if="specialRuleCategoryGroups.length > 0">
                        <h4>Mely kategóriákra érvényesek a speciális szabályok?</h4>
                        <div class="pallet-help-rule-categories" v-for="group in specialRuleCategoryGroups" :key="group.code">
                            <span class="rule-badge" :class="'rule-badge--' + group.badgeCode">{{ group.badgeLabel }}</span>
                            <div>
                                <p>{{ group.description }}</p>
                                <p class="pallet-help-category-list">
                                    {{ group.wholeGroup ? 'A teljes termékcsoportra érvényes.' : group.categories.join(', ') }}
                                </p>
                            </div>
                        </div>
                    </section>

                    <section>
                        <h4>Hogyan lesz ebből raklap darabszám?</h4>
                        <p>
                            A felső táblázat termékenként kiszámolja a normál raklap hozzájárulást. A rendszer ezeket raklap típusonként összeadja.
                            Az alsó táblázat súlyait külön összeadja, és ha eléri a beállított súly küszöböt, hozzáadja a szabályban megadott extra raklapot.
                        </p>
                        <p>
                            Ha egy termék két helyen szerepel, az nem dupla normál számítás: felül azt látod, hogy a normál raklap szabály miért adott 0-t,
                            alul pedig azt, hogy a súlya miért került mégis a súly alapú szabályba.
                        </p>
                    </section>
                </div>
            </div>
            <div class="pallet-help-layer" @click="calculationHelpOpen = false"></div>
        </div>
        <div class="modal-layer" @click="$emit('close')"></div>
    </div>
</template>

<script>
import Vue from 'vue'
import { mapGetters } from 'vuex'
import { MdIcon } from 'vue-material/dist/components'
import { calculatePalletData } from '@/plugins/palletCalculator'

Vue.use(MdIcon)

export default {
    name: 'ModalPalletDetails',
    props: {
        open: {
            type: Boolean,
            default: false,
        }
    },

    data() {
        return {
            calculationHelpOpen: false,
        }
    },

    mounted() {
        document.body.appendChild(this.$el)
    },

    beforeDestroy() {
        if (this.$el && this.$el.parentNode) {
            this.$el.parentNode.removeChild(this.$el)
        }
    },

    computed: {
        ...mapGetters(['pallets', 'orderData', 'specialPalletRules']),

        palletCalculation() {
            if (!this.orderData || !this.orderData.categories) {
                return { normalRows: [], weightRows: [], weightTotal: 0, weightRuleActive: false, weightFallbackRule: null }
            }

            return calculatePalletData(this.orderData.categories, this.pallets, this.specialPalletRules)
        },

        weightFallbackRule() {
            return this.palletCalculation.weightFallbackRule || null
        },

        palletProducts() {
            return this.palletCalculation.normalRows
        },

        weightRuleProducts() {
            return this.palletCalculation.weightRows
        },

        weightRuleTotalWeight() {
            return this.palletCalculation.weightTotal
        },

        weightRuleActive() {
            return this.palletCalculation.weightRuleActive
        },

        categoryNameById() {
            const names = {}
            const collectCategories = (categories) => {
                ;(categories || []).forEach((category) => {
                    if (category.id !== undefined && category.name) {
                        names[Number(category.id)] = category.name
                    }

                    if (category.children && category.children.length > 0) {
                        collectCategories(category.children)
                    }
                })
            }

            collectCategories(this.orderData && this.orderData.categories)

            return names
        },

        specialRuleCategoryGroups() {
            const rules = this.specialPalletRules || {}

            return [
                this.ruleCategoryGroupFor({
                    code: 'fraction_remainder',
                    badgeCode: 'fraction_remainder',
                    badgeLabel: 'Tört < 25 kg',
                    description: 'A tört raklap maradék szabály ezeknél a kategóriáknál nézi az összes tört maradék súlyát.',
                    rule: rules.fractionRemainderRule,
                }),
                this.ruleCategoryGroupFor({
                    code: 'weight_fallback',
                    badgeCode: 'weight_fallback',
                    badgeLabel: 'Súly',
                    description: 'A súly alapú szabály ezeknél a kategóriáknál gyűjti a normál raklap számításból kimaradó termékek súlyát.',
                    rule: rules.weightFallbackRule,
                }),
            ].filter(Boolean)
        },
    },

    methods: {
        ruleCategoryGroupFor({ code, badgeCode, badgeLabel, description, rule }) {
            if (!rule) {
                return null
            }

            const categoryIds = (rule.categoryIds || []).map((id) => Number(id))
            const categories = categoryIds.map((id) => this.categoryNameById[id] || `#${id}`)

            if (!rule.wholeGroup && categories.length < 1) {
                return null
            }

            return {
                code,
                badgeCode,
                badgeLabel,
                description,
                wholeGroup: rule.wholeGroup === true,
                categories,
            }
        },
    }
}
</script>

<style lang="scss" scoped>
@import '../../assets/sass/toolkit.scss';
@import '../../assets/sass/ui/button.scss';
@import '../../assets/sass/ui/modal.scss';

.modal-box--pallet-details {
    width: 1200px;
    max-width: 95vw;
    top: 5vh;
    max-height: 90vh;
    overflow-y: auto;

    @media only screen and (max-width: 900px) {
        width: 95%;
    }
}

.modal-box-body {
    padding: 24px 32px 32px;
}

.modal-title-stack {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.pallet-help-link {
    border: 0;
    background: transparent;
    padding: 0;
    color: $color-gray-5 !important;
    cursor: pointer;
    font-size: 12px;
    text-align: left;

    &:hover {
        text-decoration: underline;
    }
}

.pallet-help-overlay {
    position: fixed;
    z-index: 10001;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
}

.pallet-help-layer {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background: rgba(0, 0, 0, 0.35);
}

.pallet-help-box {
    position: relative;
    z-index: 1;
    width: 1000px;
    max-width: 95vw;
    max-height: 90vh;
    overflow-y: auto;
    background: #fff;
    border-radius: 6px;
    box-shadow: 0 12px 36px rgba(0, 0, 0, 0.2);
}

.pallet-help-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 20px 24px 14px;
    border-bottom: 1px solid $color-gray-bg-2;

    h3 {
        margin: 0 0 6px;
        font-size: 18px;
    }

    p {
        margin: 0;
        color: $color-gray-5;
        font-size: 13px;
        line-height: 1.45;
    }
}

.pallet-help-content {
    padding: 18px 24px 24px;

    section + section {
        margin-top: 20px;
    }

    h4 {
        margin: 0 0 8px;
        font-size: 13px;
        font-weight: bold;
        text-transform: uppercase;
        color: $color-gray-5;
    }

    p,
    li {
        font-size: 13px;
        line-height: 1.55;
    }

    p {
        margin: 0 0 10px;
    }

    ul {
        margin: 0;
        padding: 0;
        list-style: none;
    }

    li {
        display: grid;
        grid-template-columns: max-content 1fr;
        gap: 12px;
        align-items: start;
        padding: 8px 0;
        border-bottom: 1px solid $color-gray-bg-2;

        .rule-badge {
            justify-self: center;
            align-self: center;
        }

        &:last-child {
            border-bottom: 0;
        }
    }
}

.pallet-help-rule-categories {
    display: grid;
    grid-template-columns: max-content 1fr;
    gap: 12px;
    align-items: start;
    padding: 10px 0;
    border-bottom: 1px solid $color-gray-bg-2;

    .rule-badge {
        justify-self: center;
        align-self: center;
    }

    &:last-child {
        border-bottom: 0;
    }
}

.pallet-help-category-list {
    color: $color-gray-5;
    font-weight: bold;
}

.pallet-section {
    margin-bottom: 32px;

    &-title {
        font-size: 13px;
        font-weight: bold;
        text-transform: uppercase;
        color: $color-gray-5;
        margin-bottom: 12px;
        letter-spacing: 0.05em;
    }
}

.pallet-empty {
    p {
        color: $color-gray-5;
        font-size: 13px;
    }
}

.pallet-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    th {
        font-size: 11px;
        font-weight: bold;
        text-transform: uppercase;
        color: $color-gray-5;
        border-bottom: 2px solid $color-gray-bg-2;
        padding: 6px 8px;
        white-space: nowrap;

        &.th-tooltip {
            cursor: help;
            border-bottom: 1px dashed $color-gray-5;
            text-decoration: none;
        }
    }

    td {
        padding: 7px 8px;
        border-bottom: 1px solid $color-gray-bg-2;
        vertical-align: middle;
    }

    tbody tr:last-child td {
        border-bottom: none;
    }
}

.text-right {
    text-align: right;
}

.text-muted {
    color: $color-gray-5;
    font-size: 12px;
}

.rule-badge {
    display: inline-block;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: bold;
    white-space: nowrap;
}

.rule-badge--normal {
    background: $color-gray-bg-2;
    color: $color-gray-5;
}

.rule-badge--fraction_remainder {
    background: #e0f2f1;
    color: #00695c;
}

.rule-badge--below_threshold {
    background: #ede7f6;
    color: #4527a0;
}

.rule-badge--weight_fallback {
    background: #fff3e0;
    color: #e65100;
}

.rule-badge--threshold_weight_fallback {
    background: #ffecb3;
    color: #8a4b00;
}

.weight-rule-summary {
    margin-top: 12px;
    font-size: 13px;
    display: flex;
    gap: 24px;
    flex-wrap: wrap;
    align-items: center;
    padding: 10px 12px;
    background: $color-gray-bg-2;
    border-radius: 4px;
}

.weight-rule-active {
    color: #2e7d32;
    font-weight: bold;
}

.weight-rule-inactive {
    color: $color-gray-5;
}
</style>
